import {
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  AutocompleteInputChangeReason,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  SelectItem,
  useAutocompleteTitleOptionsLazyQuery,
} from '@kistill/client/data-access'
import { AutocompleteElement } from 'react-hook-form-mui'
import { useFormContext } from 'react-hook-form'

const matchesTitleQuery = (title: string | undefined, query: string) => {
  const t = (title ?? '').toLowerCase().trim()
  const q = (query ?? '').toLowerCase().trim()
  if (!q) return true
  if (t.startsWith(q)) return true
  const words = t.split(/\s+/).filter(Boolean)
  return words.some((w) => w.startsWith(q))
}

interface KistillFormAutocompleteTitleProps {
  name: string
  label: string
  multiple: boolean
  required?: boolean
  onChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: SelectItem[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<unknown> | undefined,
  ) => void
  selectedTitle?: SelectItem | undefined
  setSelectedTitle?: React.Dispatch<
    React.SetStateAction<SelectItem | undefined>
  >
  setSelectedDirector?: React.Dispatch<React.SetStateAction<string | undefined>>
}

export function KistillFormAutocompleteTitle({
  name,
  label,
  multiple,
  required,
  selectedTitle,
  setSelectedTitle,
  setSelectedDirector,
}: KistillFormAutocompleteTitleProps) {
  const [inputValue, setInputValue] = useState('')
  const [query, setQuery] = useState('')
  const [titleOptions, setTitleOptions] = useState<SelectItem[]>([])
  const latestRequestIdRef = useRef(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { register } = useFormContext()

  const [getTitleOptions] = useAutocompleteTitleOptionsLazyQuery({
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    // Initial list for opening dropdown without typing.
    const requestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = requestId
    void getTitleOptions({ variables: { query: '' } }).then((response) => {
      if (requestId !== latestRequestIdRef.current) return
      setTitleOptions(response.data?.titleOptions ?? [])
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTitleOptions])

  const dedupeById = (options: SelectItem[]) =>
    Array.from(new Map(options.map((x) => [x.id, x])).values())

  const allOptions = useMemo(() => {
    return dedupeById([...titleOptions, ...(selectedTitle ? [selectedTitle] : [])])
  }, [titleOptions, selectedTitle])

  const sortByQueryScore = (options: SelectItem[], currentQuery: string) => {
    const normalizedInput = currentQuery.trim().toLowerCase()
    return [...options]
      .filter((x) => matchesTitleQuery(x.title, normalizedInput))
      .sort((a, b) => {
        const aWords = (a.title ?? '').toLowerCase().split(' ')
        const bWords = (b.title ?? '').toLowerCase().split(' ')
        const aLastWord = aWords[aWords.length - 1]
        const bLastWord = bWords[bWords.length - 1]
        const aFirstWord = aWords[0]
        const bFirstWord = bWords[0]

        if (aFirstWord === normalizedInput && bFirstWord !== normalizedInput)
          return -1
        if (aFirstWord !== normalizedInput && bFirstWord === normalizedInput)
          return 1
        if (aLastWord === normalizedInput && bLastWord !== normalizedInput)
          return -1
        if (aLastWord !== normalizedInput && bLastWord === normalizedInput)
          return 1
        return (a.title ?? '').localeCompare(b.title ?? '', 'is')
      })
  }

  const handleInputChange = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    const raw = value ?? ''
    const q = raw.trim()
    setInputValue(raw)
    setQuery(q)

    // Clear cached options while the next query is in flight (including when
    // clearing to '') so stale search results do not linger.
    setTitleOptions([])

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    const requestId = latestRequestIdRef.current + 1
    latestRequestIdRef.current = requestId

    // Debounce network calls; bump id immediately so in-flight responses from
    // earlier debounced work cannot apply after newer input.
    debounceRef.current = setTimeout(() => {
      void getTitleOptions({ variables: { query: q } }).then((response) => {
        if (requestId !== latestRequestIdRef.current) return
        const mapped = response.data?.titleOptions ?? []
        setTitleOptions(dedupeById(mapped))
      })
    }, 120)
  }

  const handleAutocompleteChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: SelectItem | null,
    reason: AutocompleteChangeReason,
  ) => {
    if (setSelectedTitle) {
      if (reason === 'selectOption' && value) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { __typename, ...selected } = value // Destructure the value to exclude __typename
        setSelectedTitle(selected)
        if (setSelectedDirector && selected.extraInfo) {
          setSelectedDirector(selected.extraInfo)
        }
      } else if (reason === 'removeOption' || reason === 'clear') {
        setSelectedTitle(undefined)
      }
    }
  }

  return (
    <AutocompleteElement
      {...register('titleGuid')}
      multiple={multiple}
      required={required}
      name={name}
      label={label}
      options={allOptions}
      autocompleteProps={{
        inputValue: inputValue,
        clearOnBlur: false,
        onInputChange: handleInputChange,
        filterOptions: (options) => {
          const deduped = dedupeById(options as SelectItem[])
          if (!query) return deduped
          return sortByQueryScore(deduped, query)
        },
        getOptionLabel: (option) => option?.title,
        renderOption: (props, option, _state) => (
          <li {...props} key={option?.id}>
            {option?.title}
          </li>
        ),
        onChange: handleAutocompleteChange,
      }}
    />
  )
}
